<template>
  <div class="container-fluid py-5">
    <div class="row justify-content-between align-items-center mb-4">
      <div class="col">
        <h1>ESO Mapping</h1>
      </div>
      <div class="col-auto d-flex gap-3 align-items-center">
        <div class="d-flex align-items-center gap-2">
          <label class="small fw-semibold mb-0" :for="monthId">Month</label>
          <MonthPickerComponent
            v-model="selectedMonthYear"
            :inputId="monthId"
            class="eso-month"
            @update:modelValue="handleMonthChange"
          />
        </div>
        <button class="btn btn-outline-secondary" @click="toggleColumnSelector">
          <i class="fas fa-columns me-2"></i>Manage Columns
        </button>
        <button class="btn btn-primary" @click="syncESOMeterData">
          <i class="fas fa-sync me-2"></i>Sync ESO Data
        </button>
        <button class="btn btn-primary" @click="syncGrexelProducers">
          <i class="fas fa-sync me-2"></i>Sync Grexel Producers
        </button>
        <button
          class="btn btn-outline-primary"
          @click="openCorrectReadingsModal"
        >
          <i class="fas fa-table me-2"></i>Correct Readings
        </button>
        <button
          class="btn btn-outline-primary"
          @click="toggleView"
          :class="{ active: !useUserEndpoints }"
        >
          {{
            useUserEndpoints ? "Switch to Admin View" : "Switch to User View"
          }}
        </button>
      </div>
    </div>

    <!-- Setup Instructions -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">Setup Instructions</h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-6">
            <ol class="mb-0">
              <li class="mb-2">
                Go to the <a href="info" class="text-primary">Info page</a> and
                ensure you have up-to-date permissions in ESO
              </li>
              <li class="mb-2 d-flex align-items-center gap-2">
                <span>2. Click</span>
                <button
                  class="btn btn-sm btn-primary"
                  @click="syncESOMeterData"
                >
                  <i class="fas fa-sync me-1"></i>Sync ESO Data
                </button>
                <span
                  >to sync all ESO production and metering data for
                  {{ formattedSelectedMonth }}</span
                >
              </li>
              <li class="d-flex align-items-center gap-2">
                <span>3. Click</span>
                <button
                  class="btn btn-sm btn-primary"
                  @click="syncGrexelProducers"
                >
                  <i class="fas fa-sync me-1"></i>Sync Grexel Producers
                </button>
                <span>to sync all Grexel producers</span>
              </li>
            </ol>
          </div>
          <div class="col-md-6">
            <div class="alert alert-info mb-0">
              <i class="fas fa-info-circle me-2"></i>
              This view shows all Grexel metering devices that:
              <ul class="mb-0 mt-2">
                <li>We have access to</li>
                <li>Have a valid company.registry_id in our database</li>
                <li>Data displayed is for {{ formattedSelectedMonth }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Company Selection -->
    <div class="card mb-4" v-if="useUserEndpoints">
      <div class="card-body">
        <div class="row align-items-center">
          <div class="col-md-3">
            <label for="companySelect" class="form-label mb-0"
              >Select Company:</label
            >
          </div>
          <div class="col-md-9">
            <select
              id="companySelect"
              class="form-select"
              v-model="selectedCompanyId"
              @change="changeCompany"
            >
              <option value="">Select a company...</option>
              <option
                v-for="company in companies"
                :key="company.id"
                :value="company.id"
              >
                {{ company.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="polling" class="card mb-4">
      <div class="card-body">
        <LoadingIndicatorComponent message="Polling data, please wait..." />
      </div>
    </div>

    <!-- Bulk Actions -->
    <div class="card mt-3" v-if="selectedMeters.length > 0">
      <div class="card-body d-flex justify-content-between align-items-center">
        <div>
          <span class="me-3">{{ selectedMeters.length }} meters selected</span>
        </div>
        <div>
          <button class="btn btn-outline-success" @click="bulkUploadReadings">
            <i class="fas fa-upload me-1"></i>Send to Grexel
          </button>
        </div>
      </div>
    </div>

    <!-- Add this after the Bulk Actions card and before the Meters Table -->
    <div class="card mt-3 mb-4" v-if="unmatchedEntries.length > 0">
      <div class="card-body">
        <div class="d-flex align-items-center mb-3">
          <i class="fas fa-exclamation-triangle text-warning me-2"></i>
          <h5 class="mb-0">Unmatched Entries</h5>
        </div>
        <div class="table-responsive">
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th>Company Name</th>
                <th>Device Name</th>
                <th>Production Value</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="entry in unmatchedEntries"
                :key="entry.company + entry.device"
              >
                <td>{{ entry.company }}</td>
                <td>{{ entry.device }}</td>
                <td>{{ entry.production }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Meters Table -->
    <div class="card" v-if="meters.length && !polling">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <h5 class="mb-0">Meter Mappings</h5>
        <div class="input-group" style="width: 300px">
          <input
            type="text"
            class="form-control"
            v-model="searchQuery"
            placeholder="Search company, address, or meter..."
          />
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th class="text-center" style="width: 40px">
                <input
                  type="checkbox"
                  class="form-check-input"
                  v-model="selectAll"
                  @change="toggleSelectAll"
                />
              </th>
              <th
                v-if="selectedColumns['company_name']"
                @click="sort('company_name')"
                class="sortable"
              >
                Company Name
                <i class="fas" :class="getSortIcon('company_name')"></i>
              </th>
              <th
                v-if="selectedColumns['registryDeviceName']"
                @click="sort('registryDeviceName')"
                class="sortable"
              >
                Registry Device
                <i class="fas" :class="getSortIcon('registryDeviceName')"></i>
              </th>
              <th
                v-if="selectedColumns['eso_production']"
                @click="sort('eso_production')"
                class="sortable"
              >
                ESO Production ({{ formattedSelectedMonth }})
                <i class="fas" :class="getSortIcon('eso_production')"></i>
              </th>
              <th
                v-if="selectedColumns['grexel_production']"
                @click="sort('grexel_production')"
                class="sortable"
              >
                Grexel Production ({{ formattedSelectedMonth }})
                <i class="fas" :class="getSortIcon('grexel_production')"></i>
              </th>
              <th style="width: 120px">Actions</th>
              <template v-for="field in allFields" :key="field.key">
                <th
                  v-if="
                    selectedColumns[field.key] &&
                    ![
                      'company_name',
                      'registryDeviceName',
                      'eso_production',
                      'grexel_production',
                    ].includes(field.key)
                  "
                  @click="sort(field.key)"
                  class="sortable"
                >
                  {{ field.label }}
                  <i class="fas" :class="getSortIcon(field.key)"></i>
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="meter in sortedMeters"
              :key="meter.grid_reference"
              :class="{
                'bg-warning bg-opacity-50': updatedGridReferences.has(
                  meter.grid_reference
                ),
              }"
            >
              <td class="text-center">
                <input
                  type="checkbox"
                  class="form-check-input"
                  v-model="selectedMeters"
                  :value="meter.grid_reference"
                />
              </td>
              <td v-if="selectedColumns['company_name']">
                {{ formatFieldValue(meter.company_name) }}
              </td>
              <td v-if="selectedColumns['registryDeviceName']">
                {{ formatFieldValue(meter.registryDeviceName) }}
              </td>
              <td v-if="selectedColumns['eso_production']">
                <input
                  type="number"
                  class="form-control form-control-sm"
                  :value="meter.eso_last_month_production"
                  @input="
                    (e: Event) =>
                      updateEsoProduction(
                        meter,
                        (e.target as HTMLInputElement)?.value ?? ''
                      )
                  "
                  step="0.001"
                  min="0"
                  pattern="[0-9]*[.,]?[0-9]*"
                />
              </td>
              <td v-if="selectedColumns['grexel_production']">
                {{ meter.grexel_last_month_production }}
              </td>
              <td>
                <div class="btn-group">
                  <button
                    class="btn btn-sm btn-outline-primary"
                    @click="openEditModal(meter)"
                  >
                    <i class="fas fa-edit me-1"></i>Edit
                  </button>
                  <button
                    class="btn btn-sm btn-outline-success"
                    @click="uploadReading(meter)"
                  >
                    <i class="fas fa-upload me-1"></i>Send
                  </button>
                </div>
              </td>
              <template v-for="field in allFields" :key="field.key">
                <td
                  v-if="
                    selectedColumns[field.key] &&
                    ![
                      'company_name',
                      'registryDeviceName',
                      'eso_production',
                      'grexel_production',
                    ].includes(field.key)
                  "
                >
                  <template v-if="field.key === 'address'">
                    <small>
                      {{ meter.address.street }}, {{ meter.address.city }},
                      {{ meter.address.region }}, {{ meter.address.country }}
                    </small>
                  </template>
                  <template v-else-if="field.key === 'supported'">
                    <span
                      class="badge"
                      :class="meter.supported ? 'bg-success' : 'bg-secondary'"
                    >
                      {{ meter.supported ? "Yes" : "No" }}
                    </span>
                  </template>
                  <template v-else-if="field.key === 'active'">
                    <span
                      class="badge"
                      :class="meter.active ? 'bg-success' : 'bg-secondary'"
                    >
                      {{ meter.active ? "Active" : "Inactive" }}
                    </span>
                  </template>
                  <template v-else>
                    {{ formatFieldValue(meter[field.key]) }}
                  </template>
                </td>
              </template>
            </tr>
            <tr v-if="filteredMeters.length === 0">
              <td colspan="100%" class="text-center py-4">
                No meters found for {{ formattedSelectedMonth }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <!-- Edit Modal -->
    <div
      v-if="showEditModal"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Edit Mapping</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeEditModal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-4">
              <h6 class="mb-3">ESO Meter Selection</h6>
              <div class="table-responsive">
                <table class="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Meter Number</th>
                      <th>Object Number</th>
                      <th>Address</th>
                      <th>Company Name</th>
                      <th class="text-center">Select</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="option in meterOptions"
                      :key="option.meter_number"
                      :class="{
                        'table-secondary':
                          option.is_used &&
                          option.used_by_device !==
                            selectedMeter?.grid_reference,
                      }"
                      :title="
                        option.is_used &&
                        option.used_by_device !== selectedMeter?.grid_reference
                          ? `Already used by ${option.used_by_device}`
                          : ''
                      "
                    >
                      <td>{{ option.meter_number || "-" }}</td>
                      <td>{{ option.object_number }}</td>
                      <td>{{ option.object_address }}</td>
                      <td>{{ option.company_name || "-" }}</td>
                      <td class="text-center">
                        <input
                          type="radio"
                          name="esoMeterNumber"
                          :value="option.meter_number"
                          v-model="meterNumber"
                          class="form-check-input"
                          :disabled="
                            option.is_used &&
                            option.used_by_device !==
                              selectedMeter?.grid_reference
                          "
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="form-check mb-3">
              <input
                type="checkbox"
                v-model="meterActive"
                class="form-check-input"
                id="activeStatus"
              />
              <label class="form-check-label" for="activeStatus">
                Active Status
              </label>
            </div>
            <div class="mb-3">
              <label for="mappingComment" class="form-label">Comment</label>
              <input
                type="text"
                class="form-control"
                id="mappingComment"
                v-model="mappingComment"
                placeholder="Enter a comment..."
              />
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeEditModal">
              Cancel
            </button>
            <button class="btn btn-primary" @click="saveMapping">
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Column Selector Modal -->
    <div
      class="modal fade"
      id="columnSelectorModal"
      tabindex="-1"
      ref="columnModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Manage Columns</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="form-check" v-for="field in allFields" :key="field.key">
              <input
                type="checkbox"
                class="form-check-input"
                :id="field.key"
                v-model="selectedColumns[field.key]"
                @change="saveColumnSelections"
              />
              <label class="form-check-label" :for="field.key">
                {{ field.label }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Correct Readings Modal -->
    <div
      v-if="showCorrectReadingsModal"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Correct Readings</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeCorrectReadingsModal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-info mb-3">
              <i class="fas fa-info-circle me-2"></i>
              Paste your ESO meter reading data here. Each meter reading should
              contain 7 lines with ESO value, unit, organization ID, company
              name, plant name, GSRN, and grid reference.
              <br /><br />
              <strong>Example format:</strong>
              <pre class="mt-2 mb-0" style="font-size: 0.85em">
4,137
MWh
27X8E587BP
UAB Sobeta
JODAS (Sobeta)
643002406627002347
643002406627002347

165,125
MWh
27XW35CQ72
UAB Solis Spectrum
Solis vėjo elektrinė
643002406751000783
643002406751000783</pre>
            </div>
            <div class="form-group">
              <textarea
                class="form-control font-monospace"
                v-model="correctReadingsText"
                rows="15"
                placeholder="Paste your table data here..."
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button
              class="btn btn-secondary"
              @click="closeCorrectReadingsModal"
              :disabled="processingCorrectReadings"
            >
              Cancel
            </button>
            <button
              class="btn btn-primary"
              @click="processCorrectReadings"
              :disabled="
                !correctReadingsText.trim() || processingCorrectReadings
              "
            >
              <span
                v-if="processingCorrectReadings"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{
                processingCorrectReadings
                  ? "Processing (up to 1 minute)..."
                  : "Apply Corrections"
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { goSolidApi } from "@/composables/useGoSolidApi";
import {
  useAsyncTask,
  type AsyncTaskStartResponse,
} from "@/composables/useAsyncTask";
import { Modal } from "bootstrap";
import MonthPickerComponent from "@/components/common/MonthPickerComponent.vue";
import { computed, onMounted, ref, useId, useTemplateRef, watch } from "vue";
import { useEsoSelectedMonth } from "./useEsoSelectedMonth";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

// Mirrors MeterAddress / MeterMetadata in app/api/integrations/eso/meter_metadata.py
interface Address {
  country: string;
  city: string | null;
  region: string | null;
  street: string | null;
}

interface MeterMetadata {
  company_name: string;
  address: Address;
  grid_reference: string;
  gsrn: string | null;
  capacity_electrical: number;
  technology_name_keyword: string;
  eso_meter_number: string | null;
  eso_object_number: string;
  eso_last_month_production: number;
  grexel_last_month_production: string;
  active: boolean;
  registryDeviceName: string | null;
  deviceEic: string | null;
  supported: boolean | null;
  commercialOperationDate: string | null;
  comment: string;
  // Derived here, not sent by the backend: the two production columns the column selector sorts on.
  eso_production: number;
  grexel_production: string;
}

interface MeterOption {
  meter_number: string;
  object_number: string;
  object_address: string;
  company_name: string;
  is_used: boolean;
  used_by_device: string | null;
}

interface MeterMatch {
  meter_number: string;
  production: number;
}

interface UnmatchedMeterEntry {
  company: string;
  device: string;
  production: number;
}

// Define column field type that includes all possible column keys
type ColumnField = keyof MeterMetadata;

interface ColumnDefinition {
  key: ColumnField;
  label: string;
}

const accountStore = useAccountStore();
const { globalErrorHandler } = useGlobalHandlers();
const meters = ref<MeterMetadata[]>([]);
const showEditModal = ref(false);
const selectedMeter = ref<MeterMetadata | null>(null);
const meterOptions = ref<MeterOption[]>([]);
const useUserEndpoints = ref(false);
// Only the user view picks a company, and only once its own load has answered: until then the
// store may still hold the account the session managed before.
const companiesLoaded = ref(false);
const companies = computed(() =>
  useUserEndpoints.value && companiesLoaded.value
    ? accountStore.account.companies
    : []
);
const selectedCompanyId = ref<number | null>(null);
const polling = ref<boolean>(false);
const searchQuery = ref("");
const selectedMeters = ref<string[]>([]);
const selectAll = ref(false);
const sortBy = ref<ColumnField | "">("");
const sortDesc = ref(false);
const mappingComment = ref("");

const { selectedMonthYear, selectedYear, selectedMonth } =
  useEsoSelectedMonth();
const monthId = useId();

const formattedSelectedMonth = computed(() => {
  const date = new Date(selectedYear.value, selectedMonth.value - 1);
  return date.toLocaleString("default", {
    month: "short",
    year: "numeric",
  });
});

// Column management
const columnModal = useTemplateRef<HTMLElement>("columnModal");
let bsModal: Modal | null = null;

const allFields: ColumnDefinition[] = [
  { key: "company_name", label: "Company Name" },
  { key: "registryDeviceName", label: "Registry Device" },
  { key: "eso_production", label: "ESO Production" },
  { key: "grexel_production", label: "Grexel Production" },
  { key: "comment", label: "Comment" },
  { key: "address", label: "Address" },
  { key: "gsrn", label: "GSRN" },
  { key: "grid_reference", label: "Grid Reference" },
  { key: "capacity_electrical", label: "Capacity (kW)" },
  { key: "technology_name_keyword", label: "Technology" },
  { key: "supported", label: "Supported" },
  { key: "commercialOperationDate", label: "Comm. Op Date" },
  { key: "active", label: "Status" },
  { key: "eso_meter_number", label: "ESO Meter" },
  { key: "eso_object_number", label: "Object ID" },
];

// Initialize selected columns from localStorage or set all visible by default
const selectedColumns = ref(
  JSON.parse(
    localStorage.getItem("eso_mapping_columns") ||
      JSON.stringify(
        allFields.reduce(
          (acc, field) => {
            acc[field.key] = true; // Set all columns to visible by default
            return acc;
          },
          {} as Record<string, boolean>
        )
      )
  )
);

const saveColumnSelections = () => {
  localStorage.setItem(
    "eso_mapping_columns",
    JSON.stringify(selectedColumns.value)
  );
};

const toggleColumnSelector = () => {
  if (!bsModal) {
    bsModal = new Modal(columnModal.value as HTMLElement);
  }
  bsModal.toggle();
};

const formatFieldValue = (
  value:
    | string
    | number
    | boolean
    | Address
    | { eso: number; grexel: string }
    | null
    | undefined
): string => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toString();
  if (typeof value === "object") {
    if ("street" in value) {
      // Address
      return `${value.street}, ${value.city}, ${value.region}, ${value.country}`;
    }
    if ("eso" in value) {
      // Production
      return `ESO: ${value.eso}, Grexel: ${value.grexel}`;
    }
  }
  return String(value);
};

const filteredMeters = computed(() => {
  if (!searchQuery.value) return meters.value;
  const search = searchQuery.value.toLowerCase();
  return meters.value.filter(
    (meter) =>
      meter.company_name.toLowerCase().includes(search) ||
      meter.grid_reference.toLowerCase().includes(search) ||
      meter.eso_meter_number?.toLowerCase().includes(search) ||
      meter.eso_object_number.toLowerCase().includes(search) ||
      meter.technology_name_keyword.toLowerCase().includes(search)
  );
});

// Reads the account again on each switch to the user view: another account may have been managed
// since, in this tab or another.
const fetchCompanies = async () => {
  companiesLoaded.value = false;
  if (!useUserEndpoints.value) return;
  try {
    await accountStore.reload();
    companiesLoaded.value = true;
  } catch (error: any) {
    globalErrorHandler(error);
  }
  // A company picked for an earlier account is not this one's to show.
  if (!companies.value.some(({ id }) => id === selectedCompanyId.value)) {
    selectedCompanyId.value = null;
  }
};

const fetchMappings = async () => {
  try {
    polling.value = true;
    if (!selectedCompanyId.value && useUserEndpoints.value) {
      meters.value = [];
      return;
    }
    const endpoint = useUserEndpoints.value
      ? `/api/integration/grexel/get-eso-mappings/${selectedCompanyId.value}`
      : "/api/admin/integration/grexel/get-eso-mappings";
    const response = await goSolidApi.get(endpoint, {
      params: {
        year: selectedYear.value,
        month: selectedMonth.value,
      },
    });
    if (!response.data) {
      throw new Error("No data received from server");
    }
    if (response.status !== 200) {
      throw new Error(`Server returned status ${response.status}`);
    }
    meters.value = response.data.map((meter: any) => ({
      ...meter,
      eso_production: meter.eso_last_month_production,
      grexel_production: meter.grexel_last_month_production,
    }));
  } catch (error: any) {
    globalErrorHandler(error);
    meters.value = [];
  } finally {
    polling.value = false;
  }
};

const syncTask = useAsyncTask<{
  year: number;
  month: number;
  status: string;
}>();

watch(syncTask.state, async (state) => {
  if (state === "success") {
    await fetchMappings();
    polling.value = false;
  } else if (state === "failed") {
    globalErrorHandler(
      syncTask.error.value ??
        new Error(syncTask.errorMessage.value ?? "ESO sync failed")
    );
    polling.value = false;
  }
});

const syncESOMeterData = async () => {
  if (!selectedCompanyId.value && useUserEndpoints.value) {
    meters.value = [];
    return;
  }
  // Both outcomes, a failed start included, are reported by the syncTask state watcher.
  polling.value = true;
  syncTask.reset();
  await syncTask.execute(async () => {
    const response = await goSolidApi.post<AsyncTaskStartResponse>(
      "/api/admin/lt-eso/sync-meter-data",
      null,
      {
        params: {
          year: selectedYear.value,
          month: selectedMonth.value,
        },
      }
    );
    return response.data;
  });
};

const toggleView = async () => {
  useUserEndpoints.value = !useUserEndpoints.value;
  // The user view's mappings are its company's, so they wait for the companies.
  await fetchCompanies();
  fetchMappings();
};

const changeCompany = () => {
  fetchMappings();
};

const handleMonthChange = () => {
  fetchMappings();
};

const openEditModal = async (meter: MeterMetadata) => {
  selectedMeter.value = { ...meter };
  mappingComment.value = meter.comment;
  meterNumber.value = meter.eso_meter_number ?? "";
  try {
    const endpoint = useUserEndpoints.value
      ? `/api/integration/grexel/get-eso-mappings/${selectedCompanyId.value}/${meter.grid_reference}/options`
      : `/api/admin/integration/grexel/get-eso-mappings/${meter.grid_reference}/options`;
    const response = await goSolidApi.get(endpoint);
    if (!response.data) {
      throw new Error("No data received from server");
    }
    if (response.status !== 200) {
      throw new Error(`Server returned status ${response.status}`);
    }
    const noneOption: MeterOption = {
      meter_number: "",
      object_number: "",
      object_address: "",
      company_name: "None",
      is_used: false,
      used_by_device: null,
    };
    meterOptions.value = [noneOption, ...response.data];
  } catch (error: any) {
    globalErrorHandler(error);
    closeEditModal();
  }
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  selectedMeter.value = null;
  meterOptions.value = [];
  mappingComment.value = "";
};

const saveMapping = async () => {
  if (selectedMeter.value) {
    try {
      polling.value = true;
      const { grid_reference, active } = selectedMeter.value;
      let eso_meter_number = selectedMeter.value.eso_meter_number;

      // Handle empty meter number
      if (!eso_meter_number) {
        eso_meter_number = "null";
      }

      // Create request body
      const requestData = {
        grid_reference,
        meter_id: eso_meter_number,
        active: active.toString(),
        comment: mappingComment.value ?? "",
      };

      const endpoint = useUserEndpoints.value
        ? `/api/integration/grexel/get-eso-mappings/save/${selectedCompanyId.value}`
        : "/api/admin/integration/grexel/get-eso-mappings/save";

      const response = await goSolidApi.post(endpoint, requestData);

      if (response.status !== 200) {
        throw new Error(`Server returned status ${response.status}`);
      }

      await fetchMappings();
      closeEditModal();
    } catch (error: any) {
      globalErrorHandler(error);
    } finally {
      polling.value = false;
    }
  }
};

const uploadReading = async (meter: MeterMetadata) => {
  try {
    polling.value = true;
    const endpoint = `/api/admin/integration/grexel/upload-meter-reading/${meter.eso_meter_number}`;
    const response = await goSolidApi.post(endpoint, {
      production_value: meter.eso_last_month_production,
      year: selectedYear.value,
      month: selectedMonth.value,
    });
    if (response.status !== 200) {
      throw new Error(`Server returned status ${response.status}`);
    }
    await fetchMappings();
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    polling.value = false;
  }
};

const bulkUploadReadings = async () => {
  try {
    polling.value = true;
    for (const gridRef of selectedMeters.value) {
      const meter = meters.value.find((m) => m.grid_reference === gridRef);
      if (meter?.eso_meter_number) {
        const endpoint = `/api/admin/integration/grexel/upload-meter-reading/${meter.eso_meter_number}`;
        const response = await goSolidApi.post(endpoint, {
          production_value: meter.eso_last_month_production,
          year: selectedYear.value,
          month: selectedMonth.value,
        });
        if (response.status !== 200) {
          throw new Error(
            `Server returned status ${response.status} for meter ${meter.eso_meter_number}`
          );
        }
      }
    }
    await fetchMappings();
    selectedMeters.value = [];
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    polling.value = false;
  }
};

const meterNumber = computed({
  get: () => selectedMeter.value?.eso_meter_number || "",
  set: (value: string) => {
    if (selectedMeter.value) {
      selectedMeter.value.eso_meter_number = value;
    }
  },
});

const meterActive = computed({
  get: () => selectedMeter.value?.active || false,
  set: (value: boolean) => {
    if (selectedMeter.value) {
      selectedMeter.value.active = value;
    }
  },
});

// Sorting functionality
const sort = (field: ColumnField) => {
  if (sortBy.value === field) {
    sortDesc.value = !sortDesc.value;
  } else {
    sortBy.value = field;
    sortDesc.value = false;
  }
};

const getSortIcon = (field: ColumnField) => {
  if (sortBy.value !== field) return "fa-sort";
  return sortDesc.value ? "fa-sort-down" : "fa-sort-up";
};

const sortedMeters = computed(() => {
  if (!sortBy.value) return filteredMeters.value;

  return [...filteredMeters.value].sort((a, b) => {
    const field = sortBy.value as ColumnField;
    const aVal = a[field];
    const bVal = b[field];

    // Special handling for address field
    if (field === "address") {
      const aAddr = `${a.address.street}, ${a.address.city}, ${a.address.region}`;
      const bAddr = `${b.address.street}, ${b.address.city}, ${b.address.region}`;
      return sortDesc.value
        ? bAddr.localeCompare(aAddr)
        : aAddr.localeCompare(bAddr);
    }

    // Special handling for production fields
    if (field === "eso_production") {
      const aEso = a.eso_last_month_production;
      const bEso = b.eso_last_month_production;
      return sortDesc.value ? bEso - aEso : aEso - bEso;
    }

    if (field === "grexel_production") {
      const aGrexel = parseFloat(a.grexel_last_month_production) || 0;
      const bGrexel = parseFloat(b.grexel_last_month_production) || 0;
      return sortDesc.value ? bGrexel - aGrexel : aGrexel - bGrexel;
    }

    // Handle null/undefined values
    if (aVal === null || aVal === undefined) return sortDesc.value ? 1 : -1;
    if (bVal === null || bVal === undefined) return sortDesc.value ? -1 : 1;

    // Handle different types
    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDesc.value ? bVal - aVal : aVal - bVal;
    }

    // Default string comparison
    const aStr = String(aVal).toLowerCase();
    const bStr = String(bVal).toLowerCase();
    return sortDesc.value ? bStr.localeCompare(aStr) : aStr.localeCompare(bStr);
  });
});

// Bulk actions
const toggleSelectAll = () => {
  if (selectAll.value) {
    selectedMeters.value = sortedMeters.value.map((m) => m.grid_reference);
  } else {
    selectedMeters.value = [];
  }
};

const syncGrexelProducers = async () => {
  try {
    polling.value = true;
    const endpoint = `/api/admin/integration/grexel/sync-producers`;
    const response = await goSolidApi.post(endpoint);
    if (response.status !== 200) {
      throw new Error(`Server returned status ${response.status}`);
    }
    await fetchMappings();
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    polling.value = false;
  }
};

const updateEsoProduction = (meter: MeterMetadata, value: string) => {
  // Convert string to number, handling both comma and dot as decimal separators
  const normalizedValue = value.replace(",", ".");
  const numValue = parseFloat(normalizedValue);

  if (!isNaN(numValue) && numValue >= 0) {
    meter.eso_last_month_production = numValue;
    meter.eso_production = numValue;
  }
};

// Correct readings modal state
const showCorrectReadingsModal = ref(false);
const correctReadingsText = ref("");
const updatedGridReferences = ref<Set<string>>(new Set());
const unmatchedEntries = ref<UnmatchedMeterEntry[]>([]);
const processingCorrectReadings = ref(false);

const openCorrectReadingsModal = () => {
  showCorrectReadingsModal.value = true;
  correctReadingsText.value = "";
};

const closeCorrectReadingsModal = () => {
  showCorrectReadingsModal.value = false;
  correctReadingsText.value = "";
};

const processCorrectReadings = async () => {
  try {
    processingCorrectReadings.value = true;
    unmatchedEntries.value = [];
    updatedGridReferences.value.clear();

    const response = await goSolidApi.post(
      "/api/admin/lt-eso/process-correct-readings",
      {
        text: correctReadingsText.value,
        meters: meters.value,
        year: selectedYear.value,
        month: selectedMonth.value,
      }
    );

    if (!response.data) {
      throw new Error("No data received from server");
    }

    const { matches, unmatched } = response.data;

    meters.value = meters.value.map((meter) => {
      const match = matches.find(
        (m: MeterMatch) => m.meter_number === meter.eso_meter_number
      );
      if (match) {
        meter.eso_last_month_production = match.production;
        meter.eso_production = match.production;
        updatedGridReferences.value.add(meter.grid_reference);
      }
      return meter;
    });

    unmatchedEntries.value = unmatched.map((entry: UnmatchedMeterEntry) => ({
      company: entry.company,
      device: entry.device,
      production: entry.production,
    }));

    closeCorrectReadingsModal();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    processingCorrectReadings.value = false;
  }
};

onMounted(() => {
  fetchCompanies();
  fetchMappings();
});
</script>

<style scoped lang="scss">
.eso-month {
  width: 160px;
}

.container-fluid {
  max-width: 100%;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.card {
  border-radius: 1rem;
  border: 1px solid var(--phoenix-gray-200);
  width: 100%;
}

.card-header {
  background-color: transparent;
  border-bottom: 1px solid var(--phoenix-gray-200);
  padding: 1rem;
}

.table-responsive {
  overflow-y: auto;
  overflow-x: auto;
}

.table {
  width: 100%;
  margin-bottom: 0;
}

.table th,
.table td {
  padding: 1rem;
  vertical-align: middle;
  min-width: 120px; /* Minimum width for columns */
  max-width: 300px; /* Maximum width for columns */
  white-space: normal; /* Allow text wrapping */
  word-wrap: break-word;
}

/* Add specific width for production input */
td input[type="number"] {
  min-width: 150px; /* Make input fields wider */
}

.table th {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

/* Specific column widths */
.table th:first-child {
  width: 40px;
  min-width: 40px;
  max-width: 40px;
} /* Checkbox */

.table th:last-child {
  width: 120px;
  min-width: 120px;
  max-width: 120px;
} /* Actions */

/* Modal styles */
.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}

.form-check {
  margin-bottom: 0.5rem;
  user-select: none;
}

.form-check-input {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
  user-select: none;
}

.modal {
  overflow-y: auto;
}

.btn-group .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.btn-group .btn + .btn {
  margin-left: -1px;
}

.badge {
  font-weight: 500;
}

.form-check-input:checked {
  background-color: var(--phoenix-primary);
  border-color: var(--phoenix-primary);
}

.alert-info {
  background-color: #cff4fc;
  border-color: #b6effb;
  color: #055160;
}

.btn.active {
  background-color: var(--phoenix-primary);
  color: white;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: var(--phoenix-gray-100);
}

.sortable i {
  margin-left: 0.5rem;
  color: var(--phoenix-gray-400);
}

.sortable i.fa-sort-up,
.sortable i.fa-sort-down {
  color: var(--phoenix-primary);
}

.form-check-input {
  cursor: pointer;
}

/* Mobile styles */
@media (max-width: 991px) {
  .container-fluid {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .table {
    min-width: 1400px;
    table-layout: auto;
  }

  .table th,
  .table td {
    font-size: 0.85rem;
    padding: 0.5rem;
  }
}

.bg-warning.bg-opacity-10 {
  --bs-bg-opacity: 0.1;
}

.bg-warning.bg-opacity-25 {
  --bs-bg-opacity: 0.25;
}

/* Add transition for smoother highlighting */
tr,
input {
  transition: background-color 0.3s ease;
}

.text-warning {
  color: var(--bs-warning) !important;
}

.table-secondary {
  opacity: 0.6;
}

.table-secondary td {
  color: #6c757d !important;
}

.table-secondary input[type="radio"]:disabled {
  cursor: not-allowed;
}
</style>
